import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { MuiDoubleLabel, MuiGrid, MuiModal } from "../../../../components";
import { EMPTY_VALUE_DASH, LANGUAGE_CODE } from "../../../../helpers/constants";
import { localeFields } from "../../../../helpers/constants/siteCoreKeys";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { userPreferredLanguageSelector } from "../../../../redux/slices/appState/appStateSelector";
import { LocalizedProgramTemplateNames } from "../../../createProgramTemplate/modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../../createProgramTemplate/siteCore/SiteCoreKeys";
import { ViewLocaleWrapper } from "./ViewLocale.style";
import { useGridContainer } from "../../../../hooks/useGridContainer";

interface ManageLocaleProps {
  pageId: string;
  show: boolean;
  handleCloseModal: () => void;
  englishName: string;
  localizedProgramTemplateNames: LocalizedProgramTemplateNames[];
}

export const ViewLocale: FC<ManageLocaleProps> = ({
  pageId,
  show,
  handleCloseModal,
  englishName,
  localizedProgramTemplateNames,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );
  const { commonLabels, fetchLabel } = useSiteCore();
  const { fetchLanguageDropdownValues } = useNavigationSiteCore();

  // set key value pare data and sort mandatory to first
  const mergedLanguages = useMemo(() => {
    const languages = fetchLanguageDropdownValues();
    const fields = localeFields(commonLabels);
    return languages
      .map((language) => {
        const matchingPlaceholder = fields.find(
          (placeholder) => placeholder.key === language.value,
        );
        return {
          ...language,
          ...matchingPlaceholder,
          isMandatory: userSelectedLanguage === language.value,
          isEnglish:
            language.value === LANGUAGE_CODE.EN_US &&
            userSelectedLanguage !== LANGUAGE_CODE.EN_GB,
        };
      })
      .sort((a, b) => (b.isEnglish ? 1 : 0) - (a.isEnglish ? 1 : 0))
      .sort((a, b) => (b.isMandatory ? 1 : 0) - (a.isMandatory ? 1 : 0));
  }, [fetchLanguageDropdownValues, localeFields, userSelectedLanguage]);

  const getTemplateName = (localekey: string) => {
    const template = localizedProgramTemplateNames.find(
      (data) => data.localekey === localekey,
    );

    if (template) {
      return template.name;
    }

    return EMPTY_VALUE_DASH;
  };

  return (
    <MuiModal
      open={show}
      onClose={handleCloseModal}
      title={fetchLabel(
        createProgramSiteCoreKeys.manageLocale,
        createProgramSiteCoreKeys.viewLocaleTitle,
      )}
      primaryButtonTitle={""}
      secondaryButtonTitle={commonLabels.close}
      onSecondaryButtonClick={handleCloseModal}
      className="view-locale-modal"
    >
      <ViewLocaleWrapper>
        <MuiGrid container data-testid="view-locale">
          {mergedLanguages.map(
            (
              { key, label }: any, //any with type
            ) => (
              <MuiGrid
                key={key}
                size={gridSize}
                className={gridSizeMediumClass}
              >
                <MuiDoubleLabel
                  id={`${pageId}-${key}-label`}
                  label={label}
                  value={getTemplateName(key)}
                  isValueWithEllipsis
                />
              </MuiGrid>
            ),
          )}
        </MuiGrid>
      </ViewLocaleWrapper>
    </MuiModal>
  );
};
